import { useEffect, useState, useCallback } from "react";
import ReactQuill, { UnprivilegedEditor } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Sources } from "quill";
import type { RangeStatic } from "quill";

interface InputHtmlProps {
  label?: string;
  height?: string;
  id?: string;
  value?: string;
  placeholder?: string;
  errorText?: string;
  exClassName?: string;
  exWrapClassName?: string;
  readonly?: boolean;
  disabled?: boolean;
  onChange?: (
    value: string,
    delta: any,
    source: Sources,
    editor: UnprivilegedEditor
  ) => void;
  onFocus?: (
    selection: RangeStatic,
    source: Sources,
    editor: UnprivilegedEditor
  ) => void;
  onBlur?: (
    previousSelection: RangeStatic,
    source: Sources,
    editor: UnprivilegedEditor
  ) => void;
}

// Function to sanitize HTML content
const sanitizeHtml = (html: string): string => {
  try {
    // Remove MS Word specific tags and attributes
    let cleanHtml = html
      .replace(/<(\/)*(meta|link|span|\\?xml:|st1:|o:|font)(.*?)>/gi, "")
      .replace(/class=["'][^"']*["']/gi, "")
      .replace(/style=["'][^"']*["']/gi, "")
      .replace(/<!--.*?-->/g, "")
      .replace(/<([^>]+)?\sstyle="[^"]+"([^>]+)?>/g, "<$1$2>");

    // Basic HTML tags we want to allow
    const allowedTags = [
      "p",
      "br",
      "b",
      "i",
      "u",
      "strong",
      "em",
      "h1",
      "h2",
      "h3",
      "ul",
      "ol",
      "li",
      "a",
    ];

    // Create a DOM parser to further clean the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanHtml, "text/html");

    // Remove disallowed tags while keeping their content
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_ELEMENT,
      null
    );
    const nodesToRemove = [];
    let node;
    while ((node = walker.nextNode())) {
      if (!allowedTags.includes(node.nodeName.toLowerCase())) {
        nodesToRemove.push(node);
      }
    }

    nodesToRemove.forEach((node) => {
      const parent = node.parentNode;
      while (node.firstChild) {
        parent?.insertBefore(node.firstChild, node);
      }
      parent?.removeChild(node);
    });

    return doc.body.innerHTML;
  } catch (error) {
    console.warn("Error sanitizing HTML:", error);
    return html; // Fallback to original if sanitization fails
  }
};

export default function InputFormat(props: InputHtmlProps) {
  const {
    label,
    id,
    value = "",
    placeholder,
    errorText,
    exClassName = "",
    exWrapClassName = "",
    readonly = false,
    disabled = false,
    onChange,
    onFocus,
    onBlur,
  } = props;

  const [internalValue, setInternalValue] = useState(value);
  const [lastValidValue, setLastValidValue] = useState(value);
  const [hasError, setHasError] = useState(false);

  // Update internal value when prop changes
  useEffect(() => {
    if (value !== internalValue) {
      try {
        const sanitized = sanitizeHtml(value);
        setInternalValue(sanitized);
        setLastValidValue(sanitized);
        setHasError(false);
      } catch (error) {
        console.warn("Error processing HTML content:", error);
        setInternalValue(lastValidValue);
        setHasError(true);
      }
    }
  }, [value]);

  // Quill modules for toolbar configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false, // Disable visual matching which can cause issues
    },
  };

  // Quill formats allowed
  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
  ];

  // Debounced change handler
  const handleChange = useCallback(
    (
      value: string,
      delta: any,
      source: Sources,
      editor: UnprivilegedEditor
    ) => {
      try {
        const sanitized = sanitizeHtml(value);
        setInternalValue(sanitized);
        setLastValidValue(sanitized);
        setHasError(false);

        if (onChange) {
          onChange(sanitized, delta, source, editor);
        }
      } catch (error) {
        console.warn("Error processing editor change:", error);
        setInternalValue(lastValidValue);
        setHasError(true);
      }
    },
    [onChange, lastValidValue]
  );

  return (
    <div className={`space-y-1 ${exWrapClassName}`} key={`editor-wrapper-${id}`}>
      {label && (
        <label
          className={`block text-xs ${
            disabled ? "text-action-disabled" : "text-text-title"
          }`}
        >
          {label}
        </label>
      )}
      {hasError && (
        <div className="text-warning-main text-xs mb-2">
          Some formatting was removed as it's not supported
        </div>
      )}
      <style>
        {`
          #editor-${id} {
            overflow: hidden;
            border-radius: 0.5rem;
          }
          #editor-${id} .ql-container {
            height: ${props.height || "200px"} !important;
            border: 1px solid #e0e0e0 !important;
            border-radius: 0 0 0.5rem 0.5rem;
            overflow: hidden;
          }
          #editor-${id} .ql-editor {
            height: ${props.height || "200px"} !important;
            padding: 12px;
            overflow-y: auto;
          }
          #editor-${id} .ql-toolbar {
            border: 1px solid #e0e0e0 !important;
            border-bottom: none !important;
            border-radius: 0.5rem 0.5rem 0 0;
            z-index: 1;
            position: relative;
            background: white;
          }
        `}
      </style>
      <div id={`editor-${id}`}>
        <ReactQuill
          id={id}
          value={internalValue}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          readOnly={readonly || disabled}
          modules={modules}
          formats={formats}
          className={`bg-background-paper rounded-lg ${
            disabled ? "text-action-disabled" : "text-text-primary"
          } ${exClassName}`}
        />
      </div>
      {errorText && <p className="text-error-main text-xs">{errorText}</p>}
    </div>
  );
}